/**
 * OneDrive delete document API slice. State: loading, isError, message.
 * Thunk only; no legacy action dispatches.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { deleteOneDriveDoc, deleteOneDriveDocsBulk } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import { DOC_CHECK_IN_TYPE } from "../../constants/metaData";

const userAuth = new UserAuth();

export const deleteOneDriveDocumentsBulk = createAsyncThunk(
  "oneDriveDelete/deleteBulk",
  async (
    {
      membershipType,
      teamId,
      channelId,
      tenantId,
      oneDriveRoot,
      documentIds,
      bypassSharedLock = true,
    },
    { rejectWithValue },
  ) => {
    const ids = Array.isArray(documentIds)
      ? documentIds.filter((id) => id != null && String(id).trim() !== "")
      : [];
    if (ids.length === 0) {
      return { successCount: 0, failureCount: 0, results: [] };
    }

    logger.info(
      "deleteOneDriveDocumentsBulk start",
      { count: ids.length, teamId, oneDriveRoot },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("deleteOneDriveDocumentsBulk no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }

    try {
      const { status, data } = await deleteOneDriveDocsBulk(accessToken, {
        tenantId,
        teamId,
        channelId,
        oneDriveRoot,
        membershipType,
        bypassSharedLock,
        documentIds: ids,
      });

      if (status === 200 || status === 201) {
        logger.info("deleteOneDriveDocumentsBulk success", { status }, "REDUX");
        return data ?? { successCount: ids.length, failureCount: 0, results: [] };
      }

      logger.error("deleteOneDriveDocumentsBulk non-200/201", { status }, "REDUX");
      return rejectWithValue({ message: `Bulk delete failed (${status})` });
    } catch (err) {
      logger.error("deleteOneDriveDocumentsBulk failed", err, "REDUX");
      const message =
        err?.response?.data?.message ?? err?.message ?? "Bulk delete failed";
      return rejectWithValue({ message });
    }
  },
);

export const deleteOneDriveDocument = createAsyncThunk(
  "oneDriveDelete/delete",
  async (
    {
      membershipType,
      fileId,
      teamId,
      document,
      oneDriveRoot,
      channelId,
      operationType = DOC_CHECK_IN_TYPE.checkinNewRevision,
    },
    { rejectWithValue }
  ) => {
    logger.info("deleteOneDriveDocument start", { fileId, teamId, oneDriveRoot }, "REDUX");
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("deleteOneDriveDocument no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }
    try {
      const { status } = await deleteOneDriveDoc(accessToken, {
        fileId,
        teamId,
        membershipType,
        oneDriveRoot,
        channelId,
      });

      if (status === 204 || status === 200) {
        logger.info("deleteOneDriveDocument success", { status }, "REDUX");
        return { fileId, document, operationType };
      }

      logger.error("deleteOneDriveDocument non-200/204", { status }, "REDUX");
      return rejectWithValue({ message: `Delete failed (${status})` });
    } catch (err) {
      logger.error("deleteOneDriveDocument failed", err, "REDUX");
      const message = err?.response?.data?.message ?? err?.message ?? "Delete failed";
      return rejectWithValue({ message });
    }
  }
);

const initialState = {
  loading: false,
  isError: false,
  message: "",
};

const oneDriveDeleteSlice = createSlice({
  name: "oneDriveDelete",
  initialState,
  reducers: {
    clearOneDriveDeleteError: (state) => {
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteOneDriveDocument.pending, (state) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(deleteOneDriveDocument.fulfilled, (state) => {
        state.loading = false;
        state.isError = false;
        state.message = "";
      })
      .addCase(deleteOneDriveDocument.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Unknown error";
      })
      .addCase(deleteOneDriveDocumentsBulk.pending, (state) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(deleteOneDriveDocumentsBulk.fulfilled, (state) => {
        state.loading = false;
        state.isError = false;
        state.message = "";
      })
      .addCase(deleteOneDriveDocumentsBulk.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Unknown error";
      });
  },
});

export const { clearOneDriveDeleteError } = oneDriveDeleteSlice.actions;
export default oneDriveDeleteSlice.reducer;
